import React, { createContext, useContext, useEffect, useState } from 'react';

type ThemeId = 'teal' | 'blue' | 'purple';

interface ThemeConfig {
  id: ThemeId;
  name: string;
  description: string;
  colors: {
    primary: string;
    hover: string;
    subtle: string;
  };
}

export const THEMES: Record<ThemeId, ThemeConfig> = {
  teal: {
    id: 'teal',
    name: 'Proof of Work (Teal)',
    description: 'Confirmed signal. Distinctive, precise, and unconventional.',
    colors: { primary: '#0f766e', hover: '#115e59', subtle: '#ccfbf1' }
  },
  blue: {
    id: 'blue',
    name: 'High-end Tooling (Blue)',
    description: 'Enterprise authority. Safe, structural, and deeply trusted.',
    colors: { primary: '#1d4ed8', hover: '#1e3a8a', subtle: '#dbeafe' }
  },
  purple: {
    id: 'purple',
    name: 'Identity Layer (Purple)',
    description: 'Proprietary reality. Aligns heavily with the Fingerprint metaphor.',
    colors: { primary: '#4338ca', hover: '#312e81', subtle: '#e0e7ff' }
  }
};

interface ThemeContextType {
  activeTheme: ThemeConfig;
  setTheme: (id: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Check local storage first, default to 'teal'
  const [activeThemeId, setActiveThemeId] = useState<ThemeId>(() => {
    return (localStorage.getItem('buildlog_theme') as ThemeId) || 'teal';
  });

  const activeTheme = THEMES[activeThemeId];

  useEffect(() => {
    // Inject the CSS variables directly into the DOM root
    const root = document.documentElement;
    root.style.setProperty('--accent-primary', activeTheme.colors.primary);
    root.style.setProperty('--accent-hover', activeTheme.colors.hover);
    root.style.setProperty('--accent-subtle', activeTheme.colors.subtle);
    
    // Persist choice
    localStorage.setItem('buildlog_theme', activeThemeId);
  }, [activeTheme]);

  return (
    <ThemeContext.Provider value={{ activeTheme, setTheme: setActiveThemeId }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}